/**
 * Hook para verificar permisos de módulos del usuario y empresa
 * Los permisos son administrados desde el panel admin de SmartCFO
 * 
 * La lógica de permisos es:
 * - Si el USUARIO tiene el módulo restringido -> NO tiene acceso
 * - Si la EMPRESA tiene el módulo restringido -> NO tiene acceso
 * - Si ambos tienen acceso o no tienen configuración -> SÍ tiene acceso
 */

import { useSelector } from 'react-redux';
import { useContext, useMemo, useCallback } from 'react';
import { UserContext } from 'src/context';
import { canCobranzaOnlyUserAccessRoute } from 'src/utils/cobranzaOnlyUser';
import {
  TARJETAS_CREDITO_MODULE_ID,
  resolveTarjetasCreditoAccess,
} from '../utils/tarjetasCreditoPermissions';
import {
  MULTAS_SII_MODULE_ID,
  resolveMultasSiiAccess,
} from 'src/utils/multasSiiPermissions';
import {
  isRepositorioFlujoCajaModuleId,
  resolveRepositorioFlujoCajaAccess,
} from 'src/utils/repositorioFlujoCajaPermissions';
import {
  isRepositorioHaberesVariablesModuleId,
  resolveRepositorioHaberesVariablesAccess,
  REPOSITORIO_HABERES_VARIABLES_MODULE_ID,
} from 'src/utils/repositorioHaberesVariablesPermissions';
import {
  CONSOLIDADO_F29_MODULE_ID,
  FORMULARIO_29_MODULE_ID,
  resolveF29ModuleAccess,
} from 'src/utils/f29ModulePermissions';
import {
  resolveCrearAsientoAccess,
  resolveEditarAsientoAccess,
} from 'src/utils/asientoGrantPermissions';
import { resolveProcesarNominaManualAccess } from 'src/utils/nominaGrantPermissions';
import {
  HABERES_VARIABLES_MODULE_ID,
  resolveHaberesVariablesAccess,
} from 'src/utils/haberesVariablesPermissions';
import {
  CARGAR_NUEVO_EMPLEADO_MODULE_ID,
  resolveCargarNuevoEmpleadoAccess,
} from 'src/utils/cargarNuevoEmpleadoPermissions';
import {
  VALIDADOR_LIQUIDACIONES_MODULE_ID,
  resolveValidadorLiquidacionesAccess,
} from 'src/utils/validadorLiquidacionesPermissions';
import {
  CONTRATOS_PROXIMOS_A_VENCER_MODULE_ID,
  resolveContratosProximosAVencerAccess,
} from 'src/utils/contratosProximosAVencerPermissions';
import {
  DESCARGAS_MODULE_ID,
  resolveDescargasAccess,
} from 'src/utils/descargasPermissions';
import {
  defaultStaffModuleAccess,
  resolveStaffModuleAccess,
} from 'src/utils/staffModulePermissions';

// Admin panel IDs → repositorio keys
const ADMIN_TO_REPO_KEY_MAP = {
  'repositorio_informacion_inicial': 'informacion_inicial',
  'repositorio_informes_financieros': 'informes_contables',
  'repositorio_presupuestos': 'presupuestos',
  'repositorio_certificados': 'certificados',
  'repositorio_formularios': 'formularios',
  'repositorio_libro_remuneraciones': 'libro_remuneraciones',
  'repositorio_haberes_variables': 'haberes_variables',
  'repositorio_directorio': 'informes_financieros_mensuales',
  'repositorio_auditorias': 'auditorias',
  'repositorio_cartola_diaria': 'cartola_diaria',
  'repositorio_proveedores_hoja': 'proveedores_hoja',
  'repositorio_flujo_caja_mensual': 'flujo_caja_mensual',
  'repositorio_flujo_caja_quincenal': 'flujo_caja_quincenal',
  'repositorio_flujo_caja_semanal': 'flujo_caja_semanal',
};

/** Sub-secciones del repositorio que requieren grant explícito en admin (opt-in) */
const REPO_SECTIONS_REQUIRE_EXPLICIT_GRANT = {
  repositorio_auditorias: 'auditorias',
  repositorio_cartola_diaria: 'cartola_diaria',
};

/**
 * Sub-secciones Flujo de Caja / Haberes Variables: empresa opt-in + staff default.
 * Map admin ID → child repo key (not parent).
 */
const REPO_SECTIONS_STAFF_DEFAULT = {
  repositorio_flujo_caja_mensual: 'flujo_caja_mensual',
  repositorio_flujo_caja_quincenal: 'flujo_caja_quincenal',
  repositorio_flujo_caja_semanal: 'flujo_caja_semanal',
  [REPOSITORIO_HABERES_VARIABLES_MODULE_ID]: 'haberes_variables',
};

/*
  Módulos de contabilidad bloqueados por defecto para usuarios sin permisos
  configurados. Espeja `DEFAULT_BLOCKED_MODULES` del backend
  (helpers/permission-guard.ts) y tiene que decir lo mismo: acá tenía además
  `estructuras_maestras`, que allá no está, así que el front escondía una
  pantalla a la que el backend sí daba acceso.
*/
const DEFAULT_BLOCKED_MODULES = [
  'contabilidad', 'eerr', 'eerr_contable', 'balance_8_columnas', 'balance_clasificado',
  'libro_mayor', 'libro_diario', 'asientos', 'plan_cuentas',
  'centralizacion_contable', 'libro_compra_venta', 'cierre_apertura_contable',
];

/** Impuestos (renta / DJ / cuadre): staff ON, USER_SMARTCFO OFF sin grant. */
const IMPUESTOS_STAFF_DEFAULT_MODULE_IDS = [
  'renta',
  'estado_dj',
  'cuadre_formulario_29',
];

/** Solo accesibles si el admin otorga explícitamente hasAccess (usuario, empresa o usuario×empresa) */
const MODULE_IDS_REQUIRE_EXPLICIT_GRANT = [
  'reportes_personalizados',
  'cobranza',
  'eoaf',
  'repositorio_auditorias',
  'repositorio_cartola_diaria',
  'haberes_variables',
  'descargas',
];

/**
 * Opt-in a nivel EMPRESA: el `hasAccess === true` se exige en los permisos de
 * la empresa, no del usuario. Habilitada la empresa, la ve cualquiera de sus
 * usuarios salvo denegación explícita.
 *
 * Espeja `MODULE_IDS_REQUIRE_COMPANY_GRANT` del backend. No confundir con la
 * lista de arriba: esa exige el grant a nivel usuario y dejaría afuera hasta a
 * los admins.
 *
 * Los dos primeros no llegan a esta rama —tienen su propio resolver más
 * arriba—; están en la lista para que se lea igual que el backend.
 */
const MODULE_IDS_REQUIRE_COMPANY_GRANT = [
  'haberes_variables',
  'cargar_nuevo_empleado',
  'estructuras_maestras',
  'estado_de_resultados_gestion',
  'balance_clasificado_gestion',
];

/** /configuracion#equipo — por defecto ADMIN/COLABORADOR/SUPER_COLABORADOR; resto requiere grant explícito */
export const CONFIGURACION_EQUIPO_MODULE_ID = 'configuracion_equipo';
/** /configuracion#api-key — mismos defaults que equipo */
export const CONFIGURACION_API_KEY_MODULE_ID = 'configuracion_api_key';
/** /ficha_cliente (módulo propio) — empresa habilitada por defecto; usuario ADMIN, COLABORADOR y SUPER_COLABORADOR */
export const FICHA_CLIENTE_MODULE_ID = 'ficha_cliente';
const ROLES_DEFAULT_FICHA_CLIENTE = ['ADMIN', 'COLABORADOR', 'SUPER_COLABORADOR'];
/** /avance_contable — empresa habilitada por defecto; usuario ADMIN, COLABORADOR y SUPER_COLABORADOR */
export const AVANCE_CONTABLE_MODULE_ID = 'avance_contable';
const ROLES_DEFAULT_AVANCE_CONTABLE = ['ADMIN', 'COLABORADOR', 'SUPER_COLABORADOR'];

/** Sección Automatización en /configuracion — mismos defaults; USER_SMARTCFO solo con grant admin */
export const CONFIGURACION_AUTOMATIZACIONES_MODULE_ID = 'configuracion_automatizaciones';
const ROLES_DEFAULT_CONFIGURACION_EQUIPO = ['ADMIN', 'COLABORADOR', 'SUPER_COLABORADOR'];

function defaultConfiguracionEquipoAccess(roles) {
  return (roles || []).some((r) => ROLES_DEFAULT_CONFIGURACION_EQUIPO.includes(r));
}

function defaultConfiguracionApiKeyAccess(roles) {
  return defaultConfiguracionEquipoAccess(roles);
}

function defaultConfiguracionAutomatizacionesAccess(roles) {
  return defaultConfiguracionEquipoAccess(roles);
}

/** Solo permisos de usuario; no aplica a empresa (módulo user-only en panel admin). */
function resolveConfiguracionEquipoAccess(userPermission, roles) {
  if (userPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === true) return true;
  return defaultConfiguracionEquipoAccess(roles);
}

function resolveConfiguracionApiKeyAccess(userPermission, roles) {
  if (userPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === true) return true;
  return defaultConfiguracionApiKeyAccess(roles);
}

function resolveFichaClienteAccess(
  userPermission,
  companyPermission,
  userCompanyPermission,
  roles,
) {
  if (companyPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === false) return false;
  if (userCompanyPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === true) return true;
  if (userCompanyPermission?.hasAccess === true) return true;
  return (roles || []).some((r) => ROLES_DEFAULT_FICHA_CLIENTE.includes(r));
}

function defaultAvanceContableAccess(roles) {
  return (roles || []).some((r) => ROLES_DEFAULT_AVANCE_CONTABLE.includes(r));
}

function resolveAvanceContableAccess(
  userPermission,
  companyPermission,
  userCompanyPermission,
  roles,
) {
  if (companyPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === false) return false;
  if (userCompanyPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === true) return true;
  if (userCompanyPermission?.hasAccess === true) return true;
  return defaultAvanceContableAccess(roles);
}

function resolveConfiguracionAutomatizacionesAccess(userPermission, roles) {
  if (userPermission?.hasAccess === false) return false;
  if (userPermission?.hasAccess === true) return true;
  return defaultConfiguracionAutomatizacionesAccess(roles);
}

function buildPermissionsMaps({
  userModulePermissions,
  companyModulePermissions,
  userCompanyModulePermissions,
}) {
  const userPermissionsMap = {};
  (userModulePermissions || []).forEach((perm) => {
    userPermissionsMap[perm.moduleId] = perm;
  });

  const companyPermissionsMap = {};
  (companyModulePermissions || []).forEach((perm) => {
    companyPermissionsMap[perm.moduleId] = perm;
  });

  const userCompanyPermissionsMap = {};
  (userCompanyModulePermissions || []).forEach((perm) => {
    userCompanyPermissionsMap[perm.moduleId] = perm;
  });

  return { userPermissionsMap, companyPermissionsMap, userCompanyPermissionsMap };
}

function resolveUserCompanyModulePermissions(selectedCompany, currentUserId) {
  if (!selectedCompany?.users || !currentUserId) return [];
  const userEntry = selectedCompany.users.find((u) => u.userId === currentUserId);
  return userEntry?.modulePermissions || [];
}

/**
 * Pure module access check (usuario + empresa + usuario×empresa).
 * Usado por el hook y por DefaultLandingRedirect con empresa fresca de red.
 */
export function checkHasModuleAccess(moduleId, {
  userModulePermissions = [],
  modulePermissionsLoaded = false,
  roles: authRoles = [],
  selectedCompany = null,
  currentUserId = null,
}) {
  const companyModulePermissions = selectedCompany?.modulePermissions || [];
  const userCompanyModulePermissions = resolveUserCompanyModulePermissions(
    selectedCompany,
    currentUserId,
  );
  const { userPermissionsMap, companyPermissionsMap, userCompanyPermissionsMap } =
    buildPermissionsMaps({
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
    });

  if (moduleId === 'repositorio') {
    const sectionContext = {
      userModulePermissions,
      modulePermissionsLoaded,
      roles: authRoles,
      selectedCompany,
      currentUserId,
    };
    return Object.keys(ADMIN_TO_REPO_KEY_MAP).some((sectionId) =>
      checkHasModuleAccess(sectionId, sectionContext),
    );
  }

  if (moduleId === TARJETAS_CREDITO_MODULE_ID) {
    return resolveTarjetasCreditoAccess(
      userPermissionsMap[moduleId],
      companyPermissionsMap[moduleId],
      userCompanyPermissionsMap[moduleId],
      authRoles,
    );
  }

  if (moduleId === CONFIGURACION_EQUIPO_MODULE_ID) {
    return resolveConfiguracionEquipoAccess(userPermissionsMap[moduleId], authRoles);
  }

  if (moduleId === CONFIGURACION_API_KEY_MODULE_ID) {
    return resolveConfiguracionApiKeyAccess(userPermissionsMap[moduleId], authRoles);
  }

  if (moduleId === FICHA_CLIENTE_MODULE_ID) {
    return resolveFichaClienteAccess(
      userPermissionsMap[moduleId],
      companyPermissionsMap[moduleId],
      userCompanyPermissionsMap[moduleId],
      authRoles,
    );
  }

  if (moduleId === AVANCE_CONTABLE_MODULE_ID) {
    return resolveAvanceContableAccess(
      userPermissionsMap[moduleId],
      companyPermissionsMap[moduleId],
      userCompanyPermissionsMap[moduleId],
      authRoles,
    );
  }

  if (moduleId === CONFIGURACION_AUTOMATIZACIONES_MODULE_ID) {
    return resolveConfiguracionAutomatizacionesAccess(userPermissionsMap[moduleId], authRoles);
  }

  if (moduleId === MULTAS_SII_MODULE_ID) {
    return resolveMultasSiiAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (isRepositorioFlujoCajaModuleId(moduleId)) {
    return resolveRepositorioFlujoCajaAccess(
      moduleId,
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (isRepositorioHaberesVariablesModuleId(moduleId)) {
    return resolveRepositorioHaberesVariablesAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (moduleId === FORMULARIO_29_MODULE_ID || moduleId === CONSOLIDADO_F29_MODULE_ID) {
    return resolveF29ModuleAccess(
      moduleId,
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (IMPUESTOS_STAFF_DEFAULT_MODULE_IDS.includes(moduleId)) {
    return resolveStaffModuleAccess(
      moduleId,
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (moduleId === HABERES_VARIABLES_MODULE_ID) {
    return resolveHaberesVariablesAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (moduleId === DESCARGAS_MODULE_ID) {
    return resolveDescargasAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (moduleId === CARGAR_NUEVO_EMPLEADO_MODULE_ID) {
    return resolveCargarNuevoEmpleadoAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (moduleId === VALIDADOR_LIQUIDACIONES_MODULE_ID) {
    return resolveValidadorLiquidacionesAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  if (moduleId === CONTRATOS_PROXIMOS_A_VENCER_MODULE_ID) {
    return resolveContratosProximosAVencerAccess(
      userModulePermissions,
      companyModulePermissions,
      userCompanyModulePermissions,
      authRoles,
    );
  }

  const userPermission = userPermissionsMap[moduleId];
  if (userPermission && userPermission.hasAccess === false) {
    return false;
  }

  if (
    modulePermissionsLoaded &&
    (!userModulePermissions || userModulePermissions.length === 0) &&
    DEFAULT_BLOCKED_MODULES.includes(moduleId)
  ) {
    return false;
  }

  const companyPermission = companyPermissionsMap[moduleId];
  if (companyPermission && companyPermission.hasAccess === false) {
    return false;
  }

  const userCompanyPermission = userCompanyPermissionsMap[moduleId];
  if (userCompanyPermission && userCompanyPermission.hasAccess === false) {
    return false;
  }

  if (MODULE_IDS_REQUIRE_EXPLICIT_GRANT.includes(moduleId)) {
    const explicitlyGranted =
      (userPermission && userPermission.hasAccess === true) ||
      (companyPermission && companyPermission.hasAccess === true) ||
      (userCompanyPermission && userCompanyPermission.hasAccess === true);
    if (!explicitlyGranted) {
      return false;
    }
  }

  // Opt-in por empresa: se mira SOLO la fila de la empresa. Un usuario sin
  // permiso propio entra igual si su empresa lo tiene habilitado.
  if (
    MODULE_IDS_REQUIRE_COMPANY_GRANT.includes(moduleId) &&
    companyPermission?.hasAccess !== true
  ) {
    return false;
  }

  // Contabilidad: USER_SMARTCFO solo con grant usuario/uxc; staff puede heredar empresa.
  if (DEFAULT_BLOCKED_MODULES.includes(moduleId)) {
    const userOrUxcGranted =
      userPermission?.hasAccess === true ||
      userCompanyPermission?.hasAccess === true;
    if (userOrUxcGranted) return true;
    if (companyPermission?.hasAccess === true && defaultStaffModuleAccess(authRoles)) {
      return true;
    }
    return false;
  }

  return true;
}

/**
 * Pure route access check. Pass selectedCompany from fresh companiesByUser when resolving landing.
 */
export function checkHasRouteAccess(route, context) {
  const {
    userModulePermissions = [],
    modulePermissionsLoaded = false,
    roles: authRoles = [],
    selectedCompany = null,
    currentUserId = null,
  } = context || {};

  if (!canCobranzaOnlyUserAccessRoute(currentUserId, selectedCompany?.id, route)) {
    return false;
  }

  const pathOnly = route?.split('?')[0]?.split('#')[0] || route;

  // Link público de un solo uso (verificación email/RUT en backend, sin login)
  if (String(pathOnly).includes('/cargar_nuevo_empleado/invitar')) {
    return true;
  }

  const moduleId = getModuleIdForPath(pathOnly);

  if (!moduleId) {
    return true;
  }

  return checkHasModuleAccess(moduleId, {
    userModulePermissions,
    modulePermissionsLoaded,
    roles: authRoles,
    selectedCompany,
    currentUserId,
  });
}

/**
 * Resolve permission moduleId from a portal pathname (no query/hash).
 * Used by route guards and Heap module-view tracking.
 */
export function getModuleIdForPath(route) {
  const pathOnly = route?.split('?')[0]?.split('#')[0] || route || '';

  let moduleId = ROUTE_TO_MODULE[pathOnly] ?? ROUTE_TO_MODULE[route];

  if (!moduleId) {
    const basePath =
      '/' + String(pathOnly || '').replace(/^\//, '').split('/')[0].split(':')[0];
    moduleId = ROUTE_TO_MODULE[basePath];
  }

  return moduleId || null;
}

// Mapeo de rutas a módulos del sistema de permisos
const ROUTE_TO_MODULE = {
  '/dashboard': 'dashboard',
  '/': 'dashboard',
  
  // Reportes
  '/reportes_ventas': 'reportes_ventas',
  '/reportes_compras': 'reportes_compras',
  '/reportes_cobranza': 'reportes_cobranza',
  '/reportes_proveedores': 'reportes_proveedores',
  '/reportes': 'reportes_ventas', // fallback a ventas
  '/reportes_dj': 'reportes_dj',
  '/reportes_rrhh': 'reportes_rrhh',
  // Informes HTML personalizados — permiso dedicado en panel admin (moduleId)
  '/reportes_personalizados': 'reportes_personalizados',
  
  // Finanzas
  '/flujo_caja': 'flujo_caja',
  '/eerr': 'eerr',
  '/eoaf': 'eoaf',
  '/creditos': 'creditos',
  
  // Documentos
  '/documentos_tributarios': 'documentos_tributarios',
  '/boletas': 'boletas',
  '/obligaciones': 'obligaciones',
  '/declaraciones_de_ingresos': 'din',
  
  // Operaciones
  '/conciliacion': 'conciliacion',
  '/cobranza': 'cobranza',
  '/pago_proveedores': 'pago_proveedores',
  '/formulario-29': 'formulario_29',
  '/consolidado-f29': 'consolidado_f29',
  '/multas-sii': 'multas_sii',
  '/estado_dj': 'estado_dj',
  '/renta': 'renta',
  '/cuadre_formulario_29': 'cuadre_formulario_29',
  '/estado-observacion': 'consolidado_f29',
  '/cuadre_cartolas': 'cuadre_cartolas',
  '/tarjetas_credito': 'tarjetas_credito',
  // Contabilidad
  '/balance_8_columnas': 'balance_8_columnas',
  '/balance_clasificado': 'balance_clasificado',
  '/estado_de_resultados_contable': 'eerr_contable',
  '/libro_mayor': 'libro_mayor',
  '/libro_diario': 'libro_diario',
  '/asientos': 'asientos',
  '/plan_de_cuentas': 'plan_cuentas',
  /*
    Tres módulos SEPARADOS: una empresa puede tener el balance de gestión y no el
    estado de resultados, o solo la pantalla de configuración. Un módulo único
    hacía que conceder uno concediera los tres.
  */
  '/estructuras_maestras': 'estructuras_maestras',
  '/estado_de_resultados_gestion': 'estado_de_resultados_gestion',
  '/balance_clasificado_gestion': 'balance_clasificado_gestion',
  '/centralizacion_contable': 'centralizacion_contable',
  '/libro-compra-venta': 'libro_compra_venta',
  '/cierre_apertura_contable': 'cierre_apertura_contable',
  '/avance_contable': 'avance_contable',
  
  // Documentos
  '/cuotas_de_creditos': 'cuotas_creditos',

  // Otros
  '/repositorio': 'repositorio',
  '/din': 'din',
  '/softlanding': 'softlanding',

  // Recursos Humanos
  '/haberes_variables': 'haberes_variables',
  '/cargar_nuevo_empleado': 'cargar_nuevo_empleado',
  '/validador_liquidaciones': 'validador_liquidaciones',
  '/contratos_proximos_a_vencer': 'contratos_proximos_a_vencer',

  // Ajustes
  '/conexiones': 'conexiones',
  '/agenda': 'agenda',
  '/configuracion': 'configuracion',
  '/ficha_cliente': 'ficha_cliente',
  '/configuracion2': 'configuracion',
  '/usuarios': 'configuracion',
  '/descargas': 'descargas',
};

/**
 * Hook principal para verificar permisos de módulos (usuario + empresa)
 * @returns {Object} Objeto con funciones para verificar permisos
 */
export const useModulePermissions = () => {
  // Permisos del usuario (nivel 1 - general, desde auth/redux)
  const {
    modulePermissions: userModulePermissions,
    uid: currentUserId,
    modulePermissionsLoaded,
    roles: authRoles,
  } = useSelector((state) => state.auth);
  // GetUserById trae los roles de Mongo. La sesión en redux a veces queda sin roles
  // y entonces un ADMIN no ve módulos cuyo default depende del rol.
  const contextRoles = useContext(UserContext)?.roles;
  const roles = (authRoles && authRoles.length > 0) ? authRoles : (contextRoles || []);
  // Empresa seleccionada con sus permisos (nivel 2 - empresa general + nivel 3 - usuario×empresa)
  const { selectedCompany } = useSelector((state) => state.companyList);

  // Nivel 2: permisos generales de la empresa seleccionada
  const companyModulePermissions = selectedCompany?.modulePermissions || [];

  // Nivel 3: permisos del usuario actual dentro de la empresa seleccionada
  const userCompanyModulePermissions = useMemo(() => {
    if (!selectedCompany?.users || !currentUserId) return [];
    const userEntry = selectedCompany.users.find((u) => u.userId === currentUserId);
    return userEntry?.modulePermissions || [];
  }, [selectedCompany, currentUserId]);

  // Crear mapa de permisos del usuario para acceso rápido
  const userPermissionsMap = useMemo(() => {
    const map = {};
    if (userModulePermissions && Array.isArray(userModulePermissions)) {
      userModulePermissions.forEach((perm) => {
        map[perm.moduleId] = perm;
      });
    }
    return map;
  }, [userModulePermissions]);

  // Crear mapa de permisos de la empresa para acceso rápido
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const companyPermissionsMap = useMemo(() => {
    const map = {};
    if (companyModulePermissions && Array.isArray(companyModulePermissions)) {
      companyModulePermissions.forEach((perm) => {
        map[perm.moduleId] = perm;
      });
    }
    return map;
  }, [companyModulePermissions]);

  // Crear mapa de permisos usuario×empresa para acceso rápido
  const userCompanyPermissionsMap = useMemo(() => {
    const map = {};
    if (userCompanyModulePermissions && Array.isArray(userCompanyModulePermissions)) {
      userCompanyModulePermissions.forEach((perm) => {
        map[perm.moduleId] = perm;
      });
    }
    return map;
  }, [userCompanyModulePermissions]);

  /**
   * Verifica si el usuario tiene acceso a un módulo específico
   * Jerarquía de 3 niveles (cualquier bloqueo prevalece):
   *   1. Usuario general (mayor prioridad)
   *   2. Empresa general
   *   3. Usuario×Empresa (más granular)
   * @param {string} moduleId - ID del módulo a verificar
   * @returns {boolean} true si tiene acceso, false si no
   */
  const permissionContext = useMemo(
    () => ({
      userModulePermissions,
      modulePermissionsLoaded,
      roles,
      selectedCompany,
      currentUserId,
    }),
    [
      userModulePermissions,
      modulePermissionsLoaded,
      roles,
      selectedCompany,
      currentUserId,
    ],
  );

  const hasModuleAccess = useCallback(
    (moduleId) => checkHasModuleAccess(moduleId, permissionContext),
    [permissionContext],
  );

  /**
   * Verifica si el usuario tiene acceso a una ruta específica
   * Soporta rutas dinámicas como /repositorio/:sectionKey → busca /repositorio
   * @param {string} route - Ruta a verificar (ej: '/flujo_caja')
   * @returns {boolean} true si tiene acceso, false si no
   */
  const hasRouteAccess = useCallback(
    (route) => checkHasRouteAccess(route, permissionContext),
    [permissionContext],
  );

  /**
   * Grants de asientos (default-deny para roles externos como USER_SMARTCFO;
   * el equipo interno los tiene por defecto). Se resuelven solo a nivel usuario,
   * igual que el enforcement de smart_back.
   */
  const canCreateAsiento = useCallback(
    () => resolveCrearAsientoAccess(userModulePermissions, authRoles),
    [userModulePermissions, authRoles],
  );

  const canEditAsiento = useCallback(
    () => resolveEditarAsientoAccess(userModulePermissions, authRoles),
    [userModulePermissions, authRoles],
  );

  /**
   * Grant de procesamiento manual de nóminas (default-deny para roles externos; el
   * equipo interno lo tiene por defecto). Gatea "Procesar Manualmente" /
   * "Reprocesar Manualmente" en /pago_proveedores → mutation processNomina.
   */
  const canProcesarNominaManual = useCallback(
    () => resolveProcesarNominaManualAccess(userModulePermissions, authRoles),
    [userModulePermissions, authRoles],
  );

  /**
   * Verifica si el usuario tiene acceso a una funcionalidad específica dentro de un módulo
   * @param {string} moduleId - ID del módulo
   * @param {string} feature - ID de la funcionalidad (ej: 'edit', 'delete', 'export')
   * @returns {boolean} true si tiene acceso, false si no
   */
  const hasFeatureAccess = (moduleId, feature) => {
    // Si no tiene acceso al módulo, no tiene acceso a ninguna funcionalidad
    if (!hasModuleAccess(moduleId)) {
      return false;
    }

    // Verificar exclusiones del usuario
    const userPermission = userPermissionsMap[moduleId];
    if (userPermission?.excludedFeatures?.includes(feature)) {
      return false;
    }

    // Verificar exclusiones de la empresa
    const companyPermission = companyPermissionsMap[moduleId];
    if (companyPermission?.excludedFeatures?.includes(feature)) {
      return false;
    }

    // Verificar exclusiones del usuario×empresa
    const userCompanyPermission = userCompanyPermissionsMap[moduleId];
    if (userCompanyPermission?.excludedFeatures?.includes(feature)) {
      return false;
    }

    return true;
  };

  /**
   * Filtra un array de rutas según los permisos del usuario
   * @param {Array} routes - Array de rutas con propiedad 'to' o 'path'
   * @returns {Array} Rutas a las que el usuario tiene acceso
   */
  const filterRoutes = (routes) => {
    return routes.filter((route) => {
      const path = route.to || route.path;
      return hasRouteAccess(path);
    });
  };

  /**
   * Obtiene todos los módulos a los que el usuario NO tiene acceso
   * Combina restricciones del usuario y de la empresa
   * @returns {Array} Lista de moduleIds bloqueados
   */
  const getBlockedModules = () => {
    const blockedSet = new Set();

    // Módulos bloqueados por el usuario
    if (userModulePermissions && userModulePermissions.length > 0) {
      userModulePermissions
        .filter((perm) => perm.hasAccess === false)
        .forEach((perm) => blockedSet.add(perm.moduleId));
    }

    // Módulos bloqueados por la empresa
    if (companyModulePermissions && companyModulePermissions.length > 0) {
      companyModulePermissions
        .filter((perm) => perm.hasAccess === false)
        .forEach((perm) => blockedSet.add(perm.moduleId));
    }

    // Módulos bloqueados por usuario×empresa
    if (userCompanyModulePermissions && userCompanyModulePermissions.length > 0) {
      userCompanyModulePermissions
        .filter((perm) => perm.hasAccess === false)
        .forEach((perm) => blockedSet.add(perm.moduleId));
    }

    return Array.from(blockedSet);
  };

  /**
   * Para repositorio, traduce IDs del admin a keys del back
   * @param {string} moduleId - ID del módulo
   * @returns {Array} Lista de features excluidas
   */
  const getExcludedFeatures = (moduleId) => {
    const userPermission = userPermissionsMap[moduleId];
    const companyPermission = companyPermissionsMap[moduleId];
    const userCompanyPermission = userCompanyPermissionsMap[moduleId];
    
    const userExcluded = Array.isArray(userPermission?.excludedFeatures) ? userPermission.excludedFeatures : [];
    const companyExcluded = Array.isArray(companyPermission?.excludedFeatures) ? companyPermission.excludedFeatures : [];
    const userCompanyExcluded = Array.isArray(userCompanyPermission?.excludedFeatures) ? userCompanyPermission.excludedFeatures : [];
    
    const excluded = [...new Set([...userExcluded, ...companyExcluded, ...userCompanyExcluded])];
    
    if (moduleId === 'repositorio') {
      // Además de excludedFeatures, los sub-módulos de repositorio se gestionan
      // como entradas independientes en modulePermissions (repositorio_*) con
      // hasAccess: false. Las incluimos como features excluidas.
      Object.keys(ADMIN_TO_REPO_KEY_MAP).forEach((adminId) => {
        const userPerm = userPermissionsMap[adminId];
        const companyPerm = companyPermissionsMap[adminId];
        const userCompanyPerm = userCompanyPermissionsMap[adminId];
        if (userPerm?.hasAccess === false || companyPerm?.hasAccess === false || userCompanyPerm?.hasAccess === false) {
          if (!excluded.includes(adminId)) excluded.push(adminId);
        }
      });
      Object.entries(REPO_SECTIONS_REQUIRE_EXPLICIT_GRANT).forEach(([adminId, repoKey]) => {
        const userPerm = userPermissionsMap[adminId];
        const companyPerm = companyPermissionsMap[adminId];
        const userCompanyPerm = userCompanyPermissionsMap[adminId];
        const explicitlyGranted =
          userPerm?.hasAccess === true ||
          companyPerm?.hasAccess === true ||
          userCompanyPerm?.hasAccess === true;
        if (!explicitlyGranted && !excluded.includes(repoKey)) {
          excluded.push(repoKey);
        }
      });
      Object.entries(REPO_SECTIONS_STAFF_DEFAULT).forEach(([adminId, repoKey]) => {
        const granted = checkHasModuleAccess(adminId, {
          userModulePermissions,
          modulePermissionsLoaded,
          roles,
          selectedCompany,
          currentUserId,
        });
        if (!granted && !excluded.includes(repoKey)) {
          excluded.push(repoKey);
        }
      });
      return excluded.map((id) => ADMIN_TO_REPO_KEY_MAP[id] || id);
    }
    return excluded;
  };

  /**
   * Filtra secciones removiendo las excluidas (y sus hijos)
   * @param {string} moduleId - ID del módulo
   * @param {Array} sections - Secciones a filtrar
   * @param {string} keyProperty - Propiedad a usar como key (default: 'key')
   * @returns {Array} Secciones filtradas
   */
  const filterAllowedSections = useCallback((moduleId, sections, keyProperty = 'key') => {
    if (!hasModuleAccess(moduleId)) return [];
    if (!Array.isArray(sections)) return [];
    const excluded = getExcludedFeatures(moduleId);
    if (excluded.length === 0) return sections;

    const filterTree = (nodes) =>
      nodes
        .filter((section) => !excluded.includes(section[keyProperty]))
        .map((section) => {
          if (!section.hasChildren || !Array.isArray(section.children)) {
            return section;
          }
          const filteredChildren = filterTree(section.children);
          return { ...section, children: filteredChildren };
        })
        .filter((section) => {
          if (section.hasChildren && Array.isArray(section.children)) {
            return section.children.length > 0;
          }
          return true;
        });

    return filterTree(sections);
  }, [userModulePermissions, companyModulePermissions, userCompanyModulePermissions, authRoles, modulePermissionsLoaded, selectedCompany, currentUserId]);

  return {
    hasModuleAccess,
    hasRouteAccess,
    hasFeatureAccess,
    filterRoutes,
    getBlockedModules,
    getExcludedFeatures,
    filterAllowedSections,
    canCreateAsiento,
    canEditAsiento,
    canProcesarNominaManual,
    modulePermissions: userModulePermissions,
    userModulePermissions,
    companyModulePermissions,
    userCompanyModulePermissions,
  };
};

export default useModulePermissions;
